import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";

import { type SeriesColor } from "@/shared/domain/dashboards/DashboardDefinition";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import {
  ChartDisplayType,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";
import {
  useAggregateMetrics,
  useInfiniteQueryMetrics,
} from "@/shared/domain/metrics/hooks";
import type { Condition } from "@/types";

import { aggregateRequestFor } from "./aggregateRequest";
import type { AxisDomain } from "./axisDomain";
import { type BucketWindow } from "./bucketWindow";
import { toGroupedSeries } from "./groupedSeries";
import styles from "./MetricChart.module.css";
import { MetricChartView, type PlotDatum } from "./MetricChartView";
import { toRawSeries } from "./rawSeries";
import { rawRequestFor } from "./rawSeriesRequest";

export interface MetricChartProps {
  metricName: string;
  orgId: string | undefined;
  period: Period;
  aggregation: NumericAggregation;
  /** The resolved window to aggregate over. A preset and a custom range
   *  arrive identically — the card never learns which it was. */
  range: TimeRange;
  /** Optional: restrict to specific device(s). */
  deviceIds?: string[];
  /** Optional: restrict to specific session(s). */
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** Line (default), bar, or scatter rendering. */
  display?: ChartDisplayType;
  /** Optional: split the plot into one series per distinct value of this
   *  field, named by the same entity-prefixed wire path the dashboard's
   *  filters use. Omit for one line over everything the query matches. */
  groupBy?: string;
  /** Optional: the span every card on the dashboard draws. Pads the axis out
   *  to those bounds so cards agree about what is on screen instead of each
   *  ending wherever its own metric last reported. */
  axisDomain?: AxisDomain;
  /** Palette slot the first series is drawn in; further series follow on from
   *  it. Omit for the automatic assignment. */
  color?: SeriesColor;
  /** Fired when a real (non-placeholder) bucket is clicked. */
  onBucketClick?: (bucket: BucketWindow) => void;
}

/**
 * Data wrapper for {@link MetricChartView}: fetches aggregate metrics, handles
 * loading/error states, gap-fills the series, and maps records to the
 * pre-formatted {@link PlotDatum}[] the pure view renders.
 */
export function MetricChart({
  metricName,
  orgId,
  period,
  aggregation,
  range,
  deviceIds,
  sessionIds,
  condition,
  display = "line",
  groupBy,
  axisDomain,
  color,
  onBucketClick,
}: MetricChartProps) {
  const request = aggregateRequestFor({
    metricName,
    period,
    aggregation,
    range,
    deviceIds,
    sessionIds,
    condition,
    groupBy,
  });

  const { data, isLoading, error } = useAggregateMetrics(request, orgId);

  // One gap-filled series per group, concatenated: the view splits them back
  // apart by `group_key` into one dataset each. An ungrouped response yields
  // exactly one series, which is the case this used to be.
  const series: PlotDatum[] = React.useMemo(
    () =>
      data
        ? toGroupedSeries(data.records, groupBy, axisDomain).flatMap(
            (s) => s.points,
          )
        : [],
    [data, groupBy, axisDomain],
  );

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load metric data: {error.message}
      </Alert>
    );
  }

  return (
    <MetricChartView
      series={series}
      metricName={metricName}
      // Every bucket carries the same definition-resolved unit, so the first
      // record is representative.
      unit={data?.records[0]?.unit ?? null}
      display={display}
      color={color}
      groupBy={groupBy}
      onDatumClick={(datum) =>
        onBucketClick?.({
          start_time: datum.start_time,
          end_time: datum.end_time,
        })
      }
    />
  );
}

export interface RawMetricChartProps {
  metricName: string;
  orgId: string | undefined;
  /** Inclusive lower bound, Unix-epoch nanoseconds. */
  startTimeNs: bigint;
  /** Exclusive upper bound, Unix-epoch nanoseconds. */
  endTimeNs: bigint;
  /** Optional: restrict to specific device(s). */
  deviceIds?: string[];
  /** Optional: restrict to specific session(s). */
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** Palette slot the first series is drawn in; further series follow on from
   *  it. Omit for the automatic assignment. */
  color?: SeriesColor;
  /** Line (default) or scatter rendering. Bar is not offered on this path:
   *  the linear x-axis raw points need collapses bars to hairlines (see
   *  `drawableDisplay`), which is why the picker leaves it out — a bar that
   *  reaches here anyway is drawn as a line. */
  display?: ChartDisplayType;
  /** Optional: split the points into one series per distinct value of this
   *  field. Resolved SERVER-side — it rides on the request, and each returned
   *  data point comes back carrying its value. */
  groupBy?: string;
  /** Optional: the span every card on the dashboard draws. Pins the linear
   *  axis to those bounds, so a raw card and the aggregated card beside it
   *  show the same window rather than each fitting its axis to its own data. */
  axisDomain?: AxisDomain;
  /** Fired with the clicked point and everything the chart reported for that
   *  click — each group's nearest point, which is what the tooltip listed. A
   *  caller drilling into `selection` shows exactly those rows; see
   *  `selectionWindow`. */
  onPointClick?: (datum: PlotDatum, selection: PlotDatum[]) => void;
}

/**
 * Data wrapper for {@link MetricChartView} in RAW mode: the metric's own
 * per-session values over the window, plotted unbucketed on a
 * time-proportional x-axis.
 *
 * Runs the SAME `rawRequestFor(...)` query as the card around it, so the two
 * share one React Query cache entry and one fetch — which is what lets the
 * card's "Load all" control drain pages that land in the series drawn here.
 *
 * Deliberately does NOT gap-fill: {@link fillMissingBuckets} exists to keep an
 * evenly-spaced category axis time-proportional, and there are no uniform
 * buckets here to synthesize from. The linear axis draws the real gaps
 * instead.
 *
 * It does, however, take the same `axisDomain` the aggregated chart pads out
 * to. Gap-filling and pinning are different answers to the same question — how
 * wide is this chart's window — and the aggregated path answers it with
 * synthesized slots because a category axis has no numeric bounds to set.
 * A linear one does, so it is pinned directly.
 */
export function RawMetricChart({
  metricName,
  orgId,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
  condition,
  display = "line",
  groupBy,
  axisDomain,
  color,
  onPointClick,
}: RawMetricChartProps) {
  const request = rawRequestFor({
    metricName,
    startTimeNs,
    endTimeNs,
    deviceIds,
    sessionIds,
    condition,
    groupBy,
  });

  const { data, isLoading, error } = useInfiniteQueryMetrics(request, orgId);

  const series: PlotDatum[] = React.useMemo(
    () =>
      toRawSeries(data?.pages.flatMap((p) => p.items) ?? [], groupBy).points,
    [data, groupBy],
  );

  // Every record under one metric resolves the same definition-level unit, so
  // the first row of the first page is representative — the same reasoning the
  // aggregated chart applies to its first bucket.
  const unit = data?.pages[0]?.items[0]?.unit ?? null;

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load metric data: {error.message}
      </Alert>
    );
  }

  return (
    <MetricChartView
      series={series}
      metricName={metricName}
      unit={unit}
      display={display}
      xScale="linear"
      color={color}
      groupBy={groupBy}
      // ns -> ms: `PlotDatum.x` is epoch ms, so the scale's bounds must be
      // too. A domain bound is a whole-period boundary and therefore a whole
      // millisecond, so the division is exact.
      xDomain={
        axisDomain && {
          minMs: Number(axisDomain.startNs / 1_000_000n),
          maxMs: Number(axisDomain.endNs / 1_000_000n),
        }
      }
      onDatumClick={(datum, selection) => onPointClick?.(datum, selection)}
    />
  );
}
